import { useCallback, useEffect, useState } from 'react';

import { type TeamInviteCtaClickedLocation, useTrackTeamInviteCtaClicked } from '@air/analytics';
import { useAirModal } from '@air/provider-modal';

import { InviteMembersModal } from '~/components/Modals/InviteMembers/InviteMembersModal';
import { useTeamInviteNudgeEligibility } from '~/hooks/useTeamInviteNudgeEligibility';

const VISIBLE_SHARE_LINK_TEAM_INVITE_NUDGE_WORKSPACE_IDS = new Set<string>();

export type UseShowShareLinkTeamInviteNudgeParams = {
  location: TeamInviteCtaClickedLocation;
};

export const useShowShareLinkTeamInviteNudge = ({ location }: UseShowShareLinkTeamInviteNudgeParams) => {
  const { isTeamInviteNudgeEligibilityLoading, isTeamInviteNudgeEligible, workspaceId, workspaceName } =
    useTeamInviteNudgeEligibility();
  const { trackTeamInviteCtaClicked } = useTrackTeamInviteCtaClicked();
  const [showInviteMembersModal] = useAirModal(InviteMembersModal);
  const [isShareLinkTeamInviteNudgeVisible, setIsShareLinkTeamInviteNudgeVisible] = useState(false);

  const clearVisibleShareLinkTeamInviteNudge = useCallback(() => {
    if (workspaceId) {
      VISIBLE_SHARE_LINK_TEAM_INVITE_NUDGE_WORKSPACE_IDS.delete(workspaceId);
    }
  }, [workspaceId]);

  const hideShareLinkTeamInviteNudge = useCallback(() => {
    clearVisibleShareLinkTeamInviteNudge();
    setIsShareLinkTeamInviteNudgeVisible(false);
  }, [clearVisibleShareLinkTeamInviteNudge]);

  const openShareLinkTeamInviteNudgeModal = useCallback(() => {
    trackTeamInviteCtaClicked({ location });
    hideShareLinkTeamInviteNudge();
    showInviteMembersModal({ location: 'share-link-nudge' });
  }, [hideShareLinkTeamInviteNudge, location, showInviteMembersModal, trackTeamInviteCtaClicked]);

  useEffect(() => {
    setIsShareLinkTeamInviteNudgeVisible(false);

    return () => {
      clearVisibleShareLinkTeamInviteNudge();
    };
  }, [clearVisibleShareLinkTeamInviteNudge]);

  const showShareLinkTeamInviteNudge = useCallback(() => {
    if (!workspaceId || !isTeamInviteNudgeEligible) {
      return false;
    }

    if (VISIBLE_SHARE_LINK_TEAM_INVITE_NUDGE_WORKSPACE_IDS.has(workspaceId)) {
      setIsShareLinkTeamInviteNudgeVisible(true);
      return true;
    }

    VISIBLE_SHARE_LINK_TEAM_INVITE_NUDGE_WORKSPACE_IDS.add(workspaceId);
    setIsShareLinkTeamInviteNudgeVisible(true);

    return true;
  }, [isTeamInviteNudgeEligible, workspaceId]);

  return {
    isShareLinkTeamInviteNudgeEligibilityLoading: isTeamInviteNudgeEligibilityLoading,
    isShareLinkTeamInviteNudgeVisible,
    openShareLinkTeamInviteNudgeModal,
    showShareLinkTeamInviteNudge,
    workspaceName,
  };
};
